import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11 } from "styled-components";
import React from "react";
import { hasItems, takeFirst } from "~/features/app/utils";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { type Shape, Slider } from "~/features/ui/slider";
import { Progress } from "~/features/ui/slider/progress";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { up } from "~/features/app/screens";
import { Icon } from "~/features/ui/icon";
import CaretLeft from "~/public/icons/caret-left.svg";
import CaretRight from "~/public/icons/caret-right.svg";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type MediaCarouselBlockViewProps<TSlide = unknown> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  getSlideKey?: (slide: TSlide, index: number) => React.Key;
  hasTopDivider?: boolean | null;
  mode?: string | null;
  renderSlide: (slide: TSlide, index: number) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  slides?: TSlide[] | null;
  theme?: string | null;
};
export function MediaCarouselBlockView<TSlide = unknown>({
  anchor,
  getSlideKey,
  hasTopDivider = true,
  mode = "light",
  renderSlide,
  sectionProps,
  slides = [],
  theme = "white"
}: MediaCarouselBlockViewProps<TSlide>) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const isLg = useMediaQuery(up("lg"));
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
  const [lastSlideIndex, setLastSlideIndex] = React.useState(0);
  const sliderRef = React.useRef<Shape>(null);
  React.useEffect(() => {
    sliderRef.current?.splide?.refresh();
  }, [isLg]);
  const items = slides ?? [];
  if (!hasItems(items)) {
    return null;
  }
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[{
    "position": "relative",
    "backgroundColor": "var(--bg-secondary)"
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }]}>
      <_StyledWrap>
        <_StyledDiv $_css2={[{
        "position": "relative",
        "zIndex": "10"
      }]}>
          <Slider ref={sliderRef} onMounted={splide => setLastSlideIndex(splide.Components.Controller.getEnd())} onMove={splide => {
          setActiveSlideIndex(splide.Components.Controller.getIndex());
          setLastSlideIndex(splide.Components.Controller.getEnd());
        }} onResize={splide => setLastSlideIndex(splide.Components.Controller.getEnd())} options={{
          fixedWidth: "90%",
          autoWidth: false,
          padding: 0,
          mediaQuery: "min",
          breakpoints: {
            1024: {
              fixedWidth: undefined,
              padding: 40,
              autoWidth: true
            }
          }
        }}>
            {items.map((slide, idx) => <_StyledSliderSlide key={getSlideKey?.(slide, idx) ?? idx} $_css3={[{
            "paddingLeft": "var(--spacing-md)",
            "paddingRight": "var(--spacing-md)",
            "paddingTop": "var(--spacing-md)",
            "&:not(:last-child)": {
              "paddingRight": "0"
            },
            "@media (min-width: 1024px)": {
              "paddingLeft": "var(--spacing-md)",
              "paddingRight": "var(--spacing-md)",
              "paddingTop": "var(--spacing-xl)",
              "&:first-child": {
                "paddingLeft": "0"
              },
              "&:last-child": {
                "paddingRight": "0"
              }
            }
          }]}>
                {renderSlide(slide, idx)}
              </_StyledSliderSlide>)}
          </Slider>

          {items.length > 1 && <_StyledDiv2 $_css4={[{
          "display": "flex",
          "alignItems": "center",
          "gap": "var(--spacing-lg)",
          "paddingLeft": "var(--spacing-md)",
          "paddingRight": "var(--spacing-md)",
          "paddingTop": "var(--spacing-lg)",
          "paddingBottom": "var(--spacing-lg)",
          "@media (min-width: 1024px)": {
            "padding": "var(--spacing-xl)"
          }
        }]}>
              <_StyledDiv3>
                <Progress activeSlideIndex={activeSlideIndex} totalSlides={lastSlideIndex + 1} />
              </_StyledDiv3>
              <_StyledDiv4>
                <_StyledButton type="button" onClick={() => sliderRef.current?.go("<")} disabled={activeSlideIndex === 0} $_css5={[{
              "display": "flex",
              "height": "3.2rem",
              "width": "3.2rem",
              "alignItems": "center",
              "justifyContent": "center",
              "borderRadius": "0",
              "borderBottomLeftRadius": "4px",
              "borderTopLeftRadius": "4px",
              "backgroundColor": "var(--bg-transparent-20) !important",
              "color": "var(--text-primary)",
              "transitionProperty": "opacity"
            }]}>
                  <_StyledIcon icon={CaretLeft} size="16" $_css6={[activeSlideIndex === 0 && {
                "opacity": "0.3"
              }]} />
                </_StyledButton>
                <_StyledButton2 type="button" onClick={() => sliderRef.current?.go(">")} disabled={activeSlideIndex >= lastSlideIndex} $_css7={[{
              "display": "flex",
              "height": "3.2rem",
              "width": "3.2rem",
              "alignItems": "center",
              "justifyContent": "center",
              "borderRadius": "0",
              "borderBottomRightRadius": "4px",
              "borderTopRightRadius": "4px",
              "backgroundColor": "var(--bg-transparent-20) !important",
              "color": "var(--text-primary)",
              "transitionProperty": "opacity"
            }]}>
                  <_StyledIcon2 icon={CaretRight} size="16" $_css8={[activeSlideIndex >= lastSlideIndex && {
                "opacity": "0.3"
              }]} />
                </_StyledButton2>
              </_StyledDiv4>
            </_StyledDiv2>}
        </_StyledDiv>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-ofzw6o-0"
})`${p => p.$_css}`;
var _StyledWrap = _styled2(Wrap).withConfig({
  componentId: "sc-sf-ofzw6o-1"
})({
  "borderWidth": "0px"
});
var _StyledDiv = _styled3("div").withConfig({
  componentId: "sc-sf-ofzw6o-2"
})`${p => p.$_css2}`;
var _StyledSliderSlide = _styled4(Slider.Slide).withConfig({
  componentId: "sc-sf-ofzw6o-3"
})`${p => p.$_css3}`;
var _StyledDiv2 = _styled5("div").withConfig({
  componentId: "sc-sf-ofzw6o-4"
})`${p => p.$_css4}`;
var _StyledDiv3 = _styled6("div").withConfig({
  componentId: "sc-sf-ofzw6o-5"
})({
  "flex": "1 1 0%"
});
var _StyledDiv4 = _styled7("div").withConfig({
  componentId: "sc-sf-ofzw6o-6"
})({
  "display": "flex"
});
var _StyledButton = _styled8("button").withConfig({
  componentId: "sc-sf-ofzw6o-7"
})`${p => p.$_css5}`;
var _StyledIcon = _styled9(Icon).withConfig({
  componentId: "sc-sf-ofzw6o-8"
})`${p => p.$_css6}`;
var _StyledButton2 = _styled10("button").withConfig({
  componentId: "sc-sf-ofzw6o-9"
})`${p => p.$_css7}`;
var _StyledIcon2 = /*#__PURE__*/_styled11(Icon).withConfig({
  displayName: "media-carousel___StyledIcon2",
  componentId: "sc-75xzcw-0"
})(["", ""], p => p.$_css8);